import { PortData } from "./../1-ends/end-step-circuit-dwdm.model";
import { AfterViewInit, Component, Inject } from "@angular/core";
import { ColumnConfigType, Direction, SimpleTableColumnConfig } from "@nms-angular-toolkit/nms-simple-table";
import {
    AbstractNmsWizardStep,
    NmsWizardStepMapData,
    NmsWizardStepModel,
    NmsWizardStepValidationResponse
} from "@nms-angular-toolkit/nms-wizard";
import { ANGULARJS_TRANSLATE } from "@nms-ng2/app/shared/services/upgraded-provider/upgraded-providers";
import { Subscription } from "rxjs";
import { EndsStepModel } from "../1-ends/ends-step.model";
import { ChannelData, GridSize, TransponderPort, TranspondersStepModel } from "./transponders-step.model";
import { TransponderStepCircuitInputService } from "./services/transponder-step-circuit-input.service";
import { SelectOption } from "@nms-angular-toolkit/nms-select";
import { CircuitValidationService } from "../../../services/circuit-validation.service";
import { DwdmCircuitWizardValidationUtilsService } from "../../../services/dwdm-circuit-wizard-validation-utils.service";
import { CircuitDiagram } from "../../../diagram/models/diagram.models";
import { DwdmDiagramWizardConverterService } from "../../diagram/dwdm-diagram-wizard-converter.service";
import { LinksStepModel } from "../4-links/links-step.model";
import { AmplifiersStepModel } from "../5-amplifiers/amplifiers-step.model";
import { OscManagementStepModel } from "../6-osc-management/osc-management-step.model";
import { GeneralStepModel } from "../7-general/general-step.model";
import { IntermediatesStepModel } from "../3-intermediates/intermediates-step.model";
import { DwdmCircuitWizardUtilsService } from "../../utils/dwdm-circuit-wizard-utils.service";

/**
 * Step Transponders do DwdmCircuitWizard.
 */
@Component({
    selector: "transponders-step",
    templateUrl: "./transponders-step.component.html",
    styleUrls: ["./transponders-step.component.scss"]
})
export class TranspondersStepComponent extends AbstractNmsWizardStep implements AfterViewInit {

    model: TranspondersStepModel;
    endsModel: EndsStepModel;
    linksModel: LinksStepModel;
    intermediatesModel: IntermediatesStepModel;
    amplifiersModel: AmplifiersStepModel;
    oscsModel: OscManagementStepModel;
    generalModel: GeneralStepModel;

    subscriptions: Array<Subscription> = [];
    channelOptions: Array<SelectOption> = [];
    multirateOptions: Array<SelectOption> = [];

    private readonly MULTIRATE_DEFAULT_VALUE = "4x100G";
    private readonly TXPOWER_DEFAULT_VALUE = 0;
    private originalTransponderData: Array<{ portName: string;
        deviceId: number; originalTxPower: number}> = [];

    circuitDiagram: CircuitDiagram;

    columns: SimpleTableColumnConfig[] = [
        {
            field: "portName",
            label: this.translate.instant("dwdm.circuit.wizard.transponders.devices.list.port.title"),
            width: "40%"
        },
        {
            field: "channel",
            label: this.translate.instant("dwdm.circuit.wizard.transponders.devices.list.channel.title"),
            width: "40%",
            optionalColumn: {
                type: ColumnConfigType.SELECT_AUTO_COMPLETE,
                selectAutoCompleteOptions: this.channelOptions,
                placeholder: this.translate.instant("dwdm.circuit.wizard.ends.step.select")
            }
        },
        {
            field: "multirate",
            label: this.translate.instant("dwdm.circuit.wizard.transponders.devices.list.multirate.title"),
            width: "10%",
            optionalColumn: {
                type: ColumnConfigType.SELECT,
                selectOptions: () => this.multirateOptions,
                required: true,
                multiple: false,
                placeholder: this.translate.instant("dwdm.circuit.wizard.ends.step.select"),
                selectionAction: () => this.emitChangesWereMade(this.getCurrentStepModel())
            }
        },
        {
            field: "txPower",
            label: this.translate.instant("dwdm.circuit.wizard.transponders.devices.list.txpower.title"),
            width: "10%",
            optionalColumn: {
                type: ColumnConfigType.INPUT_NUMBER,
                label: "Tx Power",
                min: 0,
                max: 0,
                step: 0.1,
                digits: 1
            }
        }
    ];

    columnsSortable: Record<string, boolean> = {
        portName: true,
        channel: true,
        multirate: true,
        txPower: true
    };

    initialSort: Record<string, Direction> = {
        portName: "asc"
    };

    constructor(@Inject(ANGULARJS_TRANSLATE) private readonly translate: any,
            private readonly transpondersService: TransponderStepCircuitInputService,
            private readonly circuitValidationService: CircuitValidationService,
            private readonly circuitWizardValidationUtilsService: DwdmCircuitWizardValidationUtilsService,
            private readonly diagramConverterService: DwdmDiagramWizardConverterService,
            private readonly circuitWizardUtilsService: DwdmCircuitWizardUtilsService) {
        super();
    }

    ngAfterViewInit() {
        this.model = new TranspondersStepModel();
    }

    goingToNextStep(): Promise<NmsWizardStepModel> {
        this.circuitWizardUtilsService.hideDiagram.emit(true);
        return this.resolveModel();
    }

    goingToPreviousStep(): Promise<NmsWizardStepModel> {
        this.circuitWizardUtilsService.hideDiagram.emit(true);
        return this.resolveModel();
    }

    async comingFromNextStep(inputStep: NmsWizardStepModel,
            wizardStepsCacheMap: NmsWizardStepMapData): Promise<NmsWizardStepModel> {
        const model: Promise<NmsWizardStepModel> = this.resolveModel();
        await this.updateDiagramModel(wizardStepsCacheMap);

        return model;
    }

    async comingFromPreviousStep(endsStep: EndsStepModel,
            wizardStepsCacheMap: NmsWizardStepMapData): Promise<NmsWizardStepModel> {
        this.endsModel = wizardStepsCacheMap["EndsStepModel"] as EndsStepModel || this.endsModel;
        this.intermediatesModel = wizardStepsCacheMap["IntermediatesStepModel"] as IntermediatesStepModel;
        this.linksModel = wizardStepsCacheMap["LinksStepModel"] as LinksStepModel;
        this.amplifiersModel = wizardStepsCacheMap["AmplifiersStepModel"] as AmplifiersStepModel;
        this.oscsModel = wizardStepsCacheMap["OscManagementStepModel"] as OscManagementStepModel;
        this.generalModel = wizardStepsCacheMap["GeneralStepModel"] as GeneralStepModel;

        const transpondersStepModel = wizardStepsCacheMap["TranspondersStepModel"] as TranspondersStepModel;
        await this.loadOptions(endsStep);

        if (transpondersStepModel) {
            this.model = transpondersStepModel;
        } else {
            this.stepLoaded = false;
            this.model = new TranspondersStepModel();
            if (endsStep.circuitId) {
                this.model.circuitId = endsStep.circuitId;
            }
            this.stepLoaded = true;
        }

        this.setSelectedTransponderPorts(endsStep);

        if (this.model.circuitId) {
            this.saveOriginalTxPowerValues();
        }

        await this.updateDiagramModelWithCurrentValues();
        this.stepLoaded = true;

        return this.resolveModel();
    }

    async allowNext(): Promise<NmsWizardStepValidationResponse> {
        return this.validate();
    }

    async allowFinish(): Promise<NmsWizardStepValidationResponse> {
        return this.validate();
    }

    validate(): Promise<NmsWizardStepValidationResponse> {
        const isValid = this.model.aEndPorts.every(port => this.isTransponderFilled(port))
            && this.model.zEndPorts.every(port => this.isTransponderFilled(port));

        if (!isValid) {
            return Promise.reject(new NmsWizardStepValidationResponse(false,
                this.translate.instant("dwdm.circuit.wizard.transponders.step.validator.error")
            ));
        }

        const transpondersData = this.circuitWizardValidationUtilsService.buildTranspondersData(this.model, this.endsModel,
            this.channelOptions);

        return this.circuitValidationService.validateTransponders(transpondersData);
    }

    async processChanges(cache: NmsWizardStepMapData): Promise<void> {
        this.updateDiagramModel(cache);
        this.updateStepsModel(cache);
    }

    private async updateDiagramModel(cache: NmsWizardStepMapData): Promise<void> {
        this.circuitDiagram = this.diagramConverterService.convert(cache);
        this.circuitWizardUtilsService.updateDiagram.emit(this.circuitDiagram);
    }

    private async updateDiagramModelWithCurrentValues(): Promise<void> {
        await this.resolveModel();
        const stepsModel: NmsWizardStepMapData = {
            "EndsStepModel": this.endsModel,
            "TranspondersStepModel": this.model,
            "IntermediatesStepModel": this.intermediatesModel,
            "LinksStepModel": this.linksModel,
            "AmplifiersStepModel": this.amplifiersModel,
            "OscManagementStepModel": this.oscsModel,
            "GeneralStepModel": this.generalModel
        }
        this.circuitDiagram = this.diagramConverterService.convert(stepsModel);
        this.circuitWizardUtilsService.updateDiagram.emit(this.circuitDiagram);
    }

    /**
     * Realiza a atualização dos dados do step após um processamento de mudanças.
     * Quando ocorre invalidação dos steps posteriores, as propriedades que armazenam os
     * dados precisam refletir os novos valores.
     */
    private updateStepsModel(cache: NmsWizardStepMapData): Promise<void> {
        this.endsModel = cache["EndsStepModel"] as EndsStepModel;
        this.model = cache["TranspondersStepModel"] as TranspondersStepModel;
        this.intermediatesModel = cache["IntermediatesStepModel"] as IntermediatesStepModel;
        this.linksModel = cache["LinksStepModel"] as LinksStepModel;
        this.amplifiersModel = cache["AmplifiersStepModel"] as AmplifiersStepModel;
        this.oscsModel = cache["OscManagementStepModel"] as OscManagementStepModel;
        this.generalModel = cache["GeneralStepModel"] as GeneralStepModel;

        return Promise.resolve();
    }

    /**
     * Retorna os dados atuais.
     * Como a cache @see NmsWizardStepMapData é atualizada apenas no botão próximo do diagrama,
     * é preciso pegar os dados atuais para garantir o funcionamento correto do processChanges.
     */
    private getCurrentStepModel() {
        return {
            "EndsStepModel": this.endsModel,
            "TranspondersStepModel": this.model,
            "IntermediatesStepModel": this.intermediatesModel,
            "LinksStepModel": this.linksModel,
            "AmplifiersStepModel": this.amplifiersModel,
            "OscManagementStepModel": this.oscsModel,
            "GeneralStepModel": this.generalModel
        };
    }

    resolveModel(): Promise<NmsWizardStepModel> {
        this.checkTxPowerChanges();
        return Promise.resolve(this.model);
    }

    private isTransponderFilled(port: TransponderPort): boolean {
        return (
            port.channel?.trim() &&
            port.multirate?.trim() &&
            port.txPower != null
        );
    }

    private async loadOptions(endsStep: EndsStepModel) {
        await this.loadChannelOptions(endsStep);
        await this.loadMultirateOptions();
        await this.loadTxPowerConfig();
    }

    private async loadChannelOptions(endsStep: EndsStepModel) {
        const gridFromChannel = endsStep.aEndHasMux ? GridSize.GRID_100GHZ : GridSize.GRID_50GHZ;

        const channels = await this.transpondersService.getChannels(gridFromChannel).toPromise();
        this.channelOptions.length = 0;
        channels.forEach((channel) => this.channelOptions.push(this.channelToSelectOption(channel)));
    }

    private channelToSelectOption(channel: ChannelData): SelectOption {
        return {
            id: channel.channel.toString(),
            label: `${channel.channel.toFixed(1)} (${channel.frequency.toFixed(1)} THz, ${channel.waveLength.toFixed(2)} nm)`
        };
    }

    private async loadMultirateOptions() {
        const multirates = await this.transpondersService.getMultirates().toPromise();
        this.multirateOptions.length = 0;
        multirates.forEach((speed) => {
            this.multirateOptions.push({ id: speed.toString(), label: speed.toString() });
        });
    }

    private setSelectedTransponderPorts(endsStep: EndsStepModel) {
        this.model.aEndPorts = this.fillModelPorts(
            this.model.aEndDevice.id,
            this.model.aEndPorts,
            endsStep.aEndDevice.id,
            endsStep.aEndPorts
        );
        this.model.zEndPorts = this.fillModelPorts(
            this.model.zEndDevice.id,
            this.model.zEndPorts,
            endsStep.zEndDevice.id,
            endsStep.zEndPorts
        );

        this.model.aEndDevice = endsStep.aEndDevice;
        this.model.zEndDevice = endsStep.zEndDevice;
    }

    private fillModelPorts(
        modelDeviceId: number,
        modelPorts: TransponderPort[],
        endStepDeviceId: number,
        endStepPorts: PortData[]
    ): TransponderPort[] {
        if (modelDeviceId !== endStepDeviceId) {
            return endStepPorts.map((port) => this.createTransponderPortInstance(port));
        }
        return this.reloadTransponderPorts(modelPorts, endStepPorts);
    }

    /**
     *
     * Devido as configurações da coluna serem setadas na contrução do componente, as configurações que são obtidas do back,
     * por ocorrerem em momento posterior a construção, se faz necessário aplicar tais configurações diretamente no elemento do
     * array correspondente a configuração desejada, não sendo possível utilizar variável para isso por conta da perda de
     * referência.
     */
    private async loadTxPowerConfig() {
        const txPowerSettings = await this.transpondersService.getTxPowerConfig().toPromise();
        const txPowerIndex = this.columns.findIndex((item) => item.field === "txPower");
        this.columns[txPowerIndex].optionalColumn["min"] = txPowerSettings.minValue;
        this.columns[txPowerIndex].optionalColumn["max"] = txPowerSettings.maxValue;
    }

    private getMultirateDefault(): string {
        return this.multirateOptions.find((multirate) => multirate.label === this.MULTIRATE_DEFAULT_VALUE).id;
    }

    private reloadTransponderPorts(transponderPort: Array<TransponderPort>, endPorts: Array<PortData>) {
        const portsCache = new Map<string, TransponderPort>(transponderPort.map((port) => [port.portName, port]));
        transponderPort = endPorts.map((port) => this.createTransponderPortInstance(port));
        transponderPort.forEach((port) => {
            const cache = portsCache.get(port.portName);
            if (cache) {
                Object.assign(port, cache);
            }
        });
        return transponderPort;
    }

    private createTransponderPortInstance(port: PortData): TransponderPort {
        return new TransponderPort(
            port.portNo,
            port.portModel,
            port.portName,
            this.TXPOWER_DEFAULT_VALUE,
            this.getMultirateDefault()
        );
    }

    private saveOriginalTxPowerValues(): void {
        const aEndPorts = this.model.aEndPorts.map(port => ({
            portName: port.portName,
            deviceId: this.model.aEndDevice.id,
            originalTxPower: port.txPower
        }));

        const zEndPorts = this.model.zEndPorts.map(port => ({
            portName: port.portName,
            deviceId: this.model.zEndDevice.id,
            originalTxPower: port.txPower
        }));

        this.originalTransponderData = [...aEndPorts, ...zEndPorts];

    }

    private checkTxPowerChanges(): void {
        if (this.originalTransponderData.length === 0) {
            this.model.hasTxPowerChanges = false;
            return;
        }

        const aEndPorts = this.model.aEndPorts.map(port => ({
            ...port,
            deviceId: this.model.aEndDevice.id
        }));

        const zEndPorts = this.model.zEndPorts.map(port => ({
            ...port,
            deviceId: this.model.zEndDevice.id
        }));

        const allPorts = [...aEndPorts, ...zEndPorts];

        this.model.hasTxPowerChanges = allPorts.some(currentPort => {
            const original = this.originalTransponderData.find(orig =>
                orig.portName === currentPort.portName &&
                orig.deviceId === currentPort.deviceId
            );

            if (original) {
                return currentPort.txPower !== original.originalTxPower;
            }
            return false;
        });
    }

}
